
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Building, Activity, TrendingUp, Users, ShieldCheck, ArrowUpRight } from 'lucide-react';

interface SuperAdminOverviewProps {
  activeInstances: number;
  suspendedInstances: number;
  totalInstances: number;
  licenseInfo?: any;
  isVerifyingLicense?: boolean;
}

const SuperAdminOverview: React.FC<SuperAdminOverviewProps> = ({
  activeInstances,
  suspendedInstances,
  totalInstances,
  licenseInfo,
  isVerifyingLicense
}) => {
  return (
    <div className="px-4 md:px-6 py-6 space-y-6">
      <h1 className="text-2xl font-bold">Super Admin Dashboard</h1>
      
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card className="border-t-4 border-blue-500">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Total Instances</CardTitle>
            <Building className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-700">{totalInstances}</div>
            <div className="flex items-center justify-between mt-1">
              <p className="text-xs text-muted-foreground">
                {activeInstances} active, {suspendedInstances} suspended
              </p>
              <div className="flex items-center text-xs text-green-600">
                <ArrowUpRight className="h-3 w-3 mr-1" />
                <span>+12.5%</span>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-t-4 border-green-500">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Active Users</CardTitle>
            <Users className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-700">{licenseInfo?.activeUsers || 0}</div>
            <div className="flex items-center justify-between mt-1">
              <p className="text-xs text-muted-foreground">Across all instances</p>
              <div className="flex items-center text-xs text-green-600">
                <ArrowUpRight className="h-3 w-3 mr-1" />
                <span>+8.3%</span>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-t-4 border-purple-500">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">License Status</CardTitle>
            <ShieldCheck className="h-4 w-4 text-purple-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-700">
              {isVerifyingLicense ? 'Verifying...' : (licenseInfo?.status || 'Unknown')}
            </div>
            <div className="flex items-center justify-between mt-1">
              <p className="text-xs text-muted-foreground">
                {licenseInfo?.type || 'License information unavailable'}
              </p>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-t-4 border-amber-500">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Monthly Revenue</CardTitle>
            <TrendingUp className="h-4 w-4 text-amber-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-amber-700">$15,280</div>
            <div className="flex items-center justify-between mt-1">
              <p className="text-xs text-muted-foreground">This month's earnings</p>
              <div className="flex items-center text-xs text-green-600">
                <ArrowUpRight className="h-3 w-3 mr-1" />
                <span>+15.2%</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SuperAdminOverview;
